import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Popup from 'devextreme-react/popup';
import DataGrid, { Column, Editing, RequiredRule, Lookup, Button as Butt, Paging, SearchPanel, LoadPanel } from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { useApiExternalRemisions } from '../../../api/api.externalRemisions';
import { useAppSelector } from '../../../contexts/hooks';
import { useScreenSize } from '../../../utils/media-query';

interface ModalConfigLaboratoriesProps {
  visible: boolean;
  setVisible: (value: boolean) => void;
}

export const ModalConfigLaboratories = ({ visible, setVisible }: ModalConfigLaboratoriesProps) => {
  const dataGridRef = useRef<DataGrid>(null);
  const { isMedium, isSmall } = useScreenSize();
  const { getExternalLaboratories, createExternalLaboratory, updateExternalLaboratory, deleteExternalLaboratory } = useApiExternalRemisions();
  const [lstContratos, setLstContratos] = useState<any[]>([]);

  // Obtener las sedes del sistema
  const sedes = useAppSelector((data) => data.configuration.Data?.ComboBox)?.find((x) => x.Name === 'SysI_Sedes')?.Data || [];
  const Combobox = useAppSelector((x) => x.configuration.Data?.ComboBox);
  const info = useAppSelector((x) => x.configuration.Data?.SystemFields)?.filter((x) => (x.Dfc_Tabla === 'Ordenes' || x.Dfc_Tabla === 'PreOrdenes') && x.Dfc_Estado === true);

  // Cargar los contratos igual que en Orders.tsx
  useEffect(() => {
    if (info && info.length > 0 && info.filter((x) => x.Dfc_Equivalencia === 'Ord_Cn_Id').length > 0) {
      const contratos = info.filter((x) => x.Dfc_Equivalencia === 'Ord_Cn_Id')[0].Dfc_Tbc_Id;
      const contratosData = Combobox?.find((x) => x.Id === contratos)?.Data || [];
      setLstContratos(contratosData);
      return;
    }
    setLstContratos([]);
  }, [info, Combobox]);

  const customData = useMemo(() => {
    const customStore = new CustomStore({
      key: 'Rel_Id',
      load: () =>
        getExternalLaboratories()
          .then((res) => ({
            data: res,
            totalCount: res?.length || 0,
          }))
          .catch(() => {
            throw new Error('Error de carga de laboratorios externos');
          }),
      insert: async (values) => {
        // Asegurar que el estado sea true por defecto si no se especificó
        const dataToInsert = { ...values, Rel_Estado: values.Rel_Estado ?? true };
        return createExternalLaboratory(dataToInsert)
          .then((res) => {
            if (res) {
              dataGridRef.current?.instance.refresh();
              return dataToInsert;
            }
            throw new Error('Error al insertar el laboratorio');
          })
          .catch((error) => {
            console.error(error);
            throw new Error('Error insertando el laboratorio');
          });
      },
      update: (key, values) => {
        return getExternalLaboratories().then((laboratories) => {
          const rowData = laboratories.find((item: any) => item.Rel_Id === key);
          const updatedData = { ...rowData, ...values };

          return updateExternalLaboratory(updatedData, key).then((res) => {
            if (res) {
              dataGridRef.current?.instance.refresh();
              return values;
            }
            throw new Error('Error al actualizar el laboratorio');
          });
        });
      },
      remove: (key) => {
        return deleteExternalLaboratory(key).then((res) => {
          if (res) {
            dataGridRef.current?.instance.refresh();
          }
        });
      },
    });

    return new DataSource({
      store: customStore,
      reshapeOnPush: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleHidden = useCallback(() => {
    setVisible(false);
  }, [setVisible]);

  // Manejar cuando se selecciona un contrato para autocompletar el nombre
  const onEditorPreparing = useCallback(
    (e: any) => {
      if (e.dataField === 'Rel_Equivalencia' && e.parentType === 'dataRow') {
        e.editorOptions.onValueChanged = (args: any) => {
          e.setValue(args.value);
          // Buscar el contrato seleccionado y poner su nombre
          const contratoSeleccionado = lstContratos.find((c) => c.Cn_Id === args.value);
          if (contratoSeleccionado && e.row?.data) {
            // Actualizar el nombre en la fila
            e.component.cellValue(e.row.rowIndex, 'Rel_Nombre', contratoSeleccionado.Cn_Nombre);
          }
        };
      }
    },
    [lstContratos]
  );

  // Inicializar valores por defecto al agregar nueva fila
  const onInitNewRow = useCallback((e: any) => {
    e.data.Rel_Estado = true;
  }, []);

  return (
    <Popup visible={visible} onHidden={handleHidden} title='Configuración de Laboratorios Externos' showTitle={true} showCloseButton={true} width={900} height='auto' maxHeight='90%' fullScreen={isMedium || isSmall}>
      <DataGrid className='grid custom-datagrid' height={500} hoverStateEnabled={true} dataSource={customData} ref={dataGridRef} showColumnLines={true} rowAlternationEnabled={true} highlightChanges={true} repaintChangesOnly={true} width='100%' onEditorPreparing={onEditorPreparing} onInitNewRow={onInitNewRow}>
        <LoadPanel enabled={true} />
        <SearchPanel visible placeholder='Buscar' />
        <Paging enabled={false} />
        <Editing mode='popup' allowUpdating={true} allowAdding={true} allowDeleting={true} />

        <Column dataField='Rel_Sd_Id' caption='SEDE' alignment='center' width={150}>
          <Lookup dataSource={sedes} displayExpr='Sd_Nombre' valueExpr='Sd_Id' />
          <RequiredRule />
        </Column>
        <Column dataField='Rel_Equivalencia' caption='CONTRATO' alignment='center' width={180}>
          <Lookup dataSource={lstContratos} displayExpr='Cn_Nombre' valueExpr='Cn_Id' />
          <RequiredRule />
        </Column>
        <Column dataField='Rel_Nombre' caption='NOMBRE' alignment='left'>
          <RequiredRule />
        </Column>
        <Column dataField='Rel_EndPoint' caption='ENDPOINT' alignment='left'>
          <RequiredRule />
        </Column>
        <Column dataField='Rel_Token' caption='TOKEN' alignment='left' visible={false}>
          <RequiredRule />
        </Column>
        <Column dataField='Rel_Ip' caption='IP' alignment='left' width={150}>
          <RequiredRule />
        </Column>
        <Column dataField='Rel_Estado' caption='ESTADO' alignment='center' width={90} dataType='boolean' />

        <Column type='buttons' caption='ACCIÓN' width={120}>
          <Butt name='edit' />
          <Butt name='delete' />
        </Column>
      </DataGrid>
    </Popup>
  );
};
